import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { ShoppingList, ShoppingItem } from '../../../types/program';

interface ShoppingListTabProps {
  shoppingList: ShoppingList | null;
  onUpdate: (list: ShoppingList) => void;
}

const ShoppingListTab: React.FC<ShoppingListTabProps> = ({ shoppingList, onUpdate }) => {
  const addItem = () => {
    const newItem: ShoppingItem = {
      name: '',
      quantity: 1,
      unit: 'pcs',
      order: shoppingList?.items.length || 0,
    };

    onUpdate({
      ...shoppingList!,
      items: [...(shoppingList?.items || []), newItem],
    });
  };

  const updateItem = (index: number, updatedItem: ShoppingItem) => {
    const updatedItems = (shoppingList?.items || []).map((item: ShoppingItem, i: number) =>
      i === index ? updatedItem : item
    );

    onUpdate({
      ...shoppingList!,
      items: updatedItems,
    });
  };

  const deleteItem = (index: number) => {
    const updatedItems = (shoppingList?.items || []).filter((_: ShoppingItem, i: number) => i !== index);

    onUpdate({
      ...shoppingList!,
      items: updatedItems,
    });
  };

  return (
    <div className="space-y-6">
      {/* Shopping Items */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Shopping Items</h3>
          <button
            onClick={addItem}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#23F47C] text-[#0A0A0A] text-sm font-semibold rounded-lg hover:bg-[#1FD66D] transition-all"
          >
            <Plus size={16} />
            <span>Add Item</span>
          </button>
        </div>

        {(shoppingList?.items || []).map((item: ShoppingItem, index: number) => (
          <div key={index} className="bg-[#141414] rounded-xl border border-[#1F1F1F] p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-[#23F47C]">Item {index + 1}</span>
              <button
                onClick={() => deleteItem(index)}
                className="text-[#FF4757] hover:text-[#FF6B7A] p-2 hover:bg-[rgba(255,71,87,0.1)] rounded-lg transition-all"
              >
                <Trash2 size={16} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Item Name *</label>
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) =>
                    updateItem(index, { ...item, name: e.target.value })
                  }
                  placeholder="e.g., Protein Powder"
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Quantity *</label>
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      updateItem(index, { ...item, quantity: parseInt(e.target.value) || 1 })
                    }
                    min="1"
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Unit *</label>
                  <input
                    type="text"
                    value={item.unit}
                    onChange={(e) =>
                      updateItem(index, { ...item, unit: e.target.value })
                    }
                    placeholder="e.g., pcs, g, ml"
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Category</label>
                  <select
                    value={item.category || ''}
                    onChange={(e) =>
                      updateItem(index, { ...item, category: e.target.value as any })
                    }
                    className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                  >
                    <option value="">Select category</option>
                    <option value="equipment">Equipment</option>
                    <option value="supplements">Supplements</option>
                    <option value="apparel">Apparel</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Product Link (optional)</label>
                <input
                  type="url"
                  value={item.link || ''}
                  onChange={(e) =>
                    updateItem(index, { ...item, link: e.target.value })
                  }
                  placeholder="https://amazon.com/dp/..."
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#8F8F8F] mb-2">Notes (optional)</label>
                <textarea
                  value={item.notes || ''}
                  onChange={(e) =>
                    updateItem(index, { ...item, notes: e.target.value })
                  }
                  rows={2}
                  placeholder="Additional notes about this item..."
                  className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#1F1F1F] rounded-lg text-white focus:border-[#23F47C] focus:outline-none"
                />
              </div>
            </div>
          </div>
        ))}

        {(shoppingList?.items || []).length === 0 && (
          <div className="text-center py-8 text-[#8F8F8F]">
            No shopping items yet. Add your first item above.
          </div>
        )}
      </div>
    </div>
  );
};

export default ShoppingListTab;
